import dayjs from 'dayjs';
import { AsyncStorageProvider } from '../support/AsyncStorageProvider';

const STORAGE_KEY = 'get-help-prompt';

interface IGetHelpPromptData {
    showCount: number;
    lastShownAt: string | null;
    accepted: boolean;
}

const DEFAULT_DATA: IGetHelpPromptData = {
    showCount: 0,
    lastShownAt: null,
    accepted: false
};

export class GetHelpPromptStorage {
    static inject = () => [AsyncStorageProvider];

    constructor(private storageProvider: AsyncStorageProvider) {}

    private getStorage() {
        return this.storageProvider.getJSONProvider<IGetHelpPromptData>(STORAGE_KEY);
    }

    private async getData(): Promise<IGetHelpPromptData> {
        const data = await this.getStorage().get();
        return data || { ...DEFAULT_DATA };
    }

    private async setData(data: IGetHelpPromptData): Promise<void> {
        await this.getStorage().set(data);
    }

    async shouldShowPrompt(): Promise<boolean> {
        const data = await this.getData();

        // Don't show if already accepted
        if (data.accepted) {
            return false;
        }

        // Don't show if shown 3 times already
        if (data.showCount >= 3) {
            return false;
        }

        // Don't show if shown within last 2 days
        if (data.lastShownAt) {
            const daysSinceLastShown = dayjs().diff(dayjs(data.lastShownAt), 'day');
            if (daysSinceLastShown < 2) {
                return false;
            }
        }

        return true;
    }

    async markPromptShown(): Promise<void> {
        const data = await this.getData();
        data.showCount += 1;
        data.lastShownAt = dayjs().toISOString();
        await this.setData(data);
    }

    async markPromptAccepted(): Promise<void> {
        const data = await this.getData();
        data.accepted = true;
        await this.setData(data);
    }

    async getShowCount(): Promise<number> {
        const data = await this.getData();
        return data.showCount;
    }
}
